"use client";
import { orderedSizes } from '@/projectRoot/src/utils/constants/orderSizes';
import Close from '../svg/Close';
import style from './drawer.module.scss';
import LinkUrl from '../general/link/LinkUrl';
import SidebarCart from '../sidebar-cart/SidebarCart';
import Filters from '../../pages/filters-page/partials/filters/Filters';
import { useFilters } from '@/projectRoot/src/contexts/FiltersContext';
import { CSSProperties, useEffect, useMemo, useState } from 'react';
import Auth from '../auth/Auth';
import { bodyDisableScroll } from '@/projectRoot/src/utils/handlers/miscHandler';
import { useCart } from '@/projectRoot/src/contexts/CartContext';

interface Props {
    title: string;
    onCloseDrawer: () => void;
    showDrawer: boolean;
    content: any;
    content_type: 'description' | 'items' | 'table' | 'cart' | 'filters' | 'auth';
    content_position?: CSSProperties['left'] | CSSProperties['right'] | CSSProperties['top'] | CSSProperties['bottom'];
}

const Drawer = ({title, onCloseDrawer, showDrawer, content, content_type, content_position = 'right'}: Props) => {
    const {filters_count} = useFilters();
    const {cart} = useCart();
    const [count, setCount] = useState<number>();
    const [headTitle, setHeadTitle] = useState<string>('');

    const onChangeStateSetHeadTitle = (title: string) => {
        setHeadTitle(title);
    }

    const displayContent = useMemo(() => {
        switch(content_type){
            case 'description': {
                return <div dangerouslySetInnerHTML={{__html: content}} />
            }
            case 'table': {
                let standards = content.items?.map(item => item.size_standard);
                standards = Array.from(new Set(standards));
                let variants = content.items?.map(item => item.stock_variant_filter_alias);
                variants = Array.from(new Set(variants));
                const sortedVariants = (variants as string[]).map((vr) => { return { title: vr, order: orderedSizes.indexOf(vr) != -1 ? orderedSizes.indexOf(vr) : 1000 } }).sort((a, b) => a.order - b.order).map((item) => item.title) ?? [];

                const returnVariant = (standard: string, rowIndex: number) => {
                    let item = content.items?.find(item => item.stock_variant_filter_alias == sortedVariants[rowIndex] && item.size_standard == standard);
                    let stockVariant = item?.stock_variant;
                    if(stockVariant){
                        return stockVariant;
                    }else{
                        return '-';
                    }
                }
 
                return (
                    <div className={style.table}>
                        <div className={`${style.head}`}>
                            <span className={`${style.item}`}>
                                SIZE
                            </span>
                            {
                                standards &&
                                standards.map((item, index) => 
                                <span 
                                    className={`${style.item}`}
                                    key={index}
                                >
                                    {item.replaceAll('_', ' ')}
                                </span>
                                )
                            }
                        </div>
                        <div className={`${style.body}`}>
                            <div className={`${style.column}`}>
                                {
                                    sortedVariants.map((item, index) => 
                                        <span className={style.item} key={index}>
                                            {item}
                                        </span>
                                    )
                                }
                            </div>
                            {
                                standards &&
                                standards.map((standard, index) => 
                                    <div 
                                        key={index} 
                                        className={`${style.column}`}
                                    >
                                        {
                                            Array.apply(null, Array(sortedVariants.length)).map((el, index1) => 
                                                <span className={style.item} key={index1}>
                                                    {
                                                        returnVariant(standard, index1)
                                                    }
                                                </span>
                                            )
                                        }
                                    </div>
                                )
                            }
                        </div>
                    </div>
                )
            }
            case 'items': {
                return (
                    <div className={style.items}>
                        {content.map(item => 
                            <div className={style.item} key={item.id}>
                                <p className={style.title}>
                                    {item.store.title}
                                </p>
                                <LinkUrl 
                                    classes={[style.link]}
                                    text={item.store.address.street + ' ' + item.store.address.number + ', ' + item.store.address.city}
                                    reactRouter={false} 
                                    url={`https://www.google.com/maps/search/?api=1&query=${item.store.position.latitude},${item.store.position.longitude}`} 
                                />
                                <LinkUrl 
                                    classes={[style.link]}
                                    text={'Tel: ' + item.store.phone}
                                    reactRouter={false} 
                                    url={`tel: ${item.store.phone}`} 
                                />
                                <div className={style.variants}>
                                    {item.variants.map((vr) => { return { ...vr, order: orderedSizes.indexOf(vr.stock_variant) != -1 ? orderedSizes.indexOf(vr.stock_variant) : 1000 } }).sort((a, b) => a.order - b.order).map((variant, index) => <span key={index} className={style.variant}>{variant.stock_variant}</span>)}
                                </div>
                            </div>
                        ) }
                    </div>
                )
            }
            case 'cart': {
                setCount(cart ? cart.items_count : 0);
                return <SidebarCart />;
            }
            case 'filters': {
                setCount(filters_count);
                return <Filters onCloseFilters={onCloseDrawer} />;
            }
            case 'auth': {
                return <Auth setHeadTitle={onChangeStateSetHeadTitle} />;
            }
        }
    }, [content_type, cart]);

    useEffect(() => {
        bodyDisableScroll(showDrawer);
    }, [showDrawer]);

  return (
    <div className={`${style.drawerWindow} ${showDrawer ? style.visible : ''}`} onClick={onCloseDrawer}>
      <div className={style.drawerContent} onClick={(e) => e.stopPropagation()} style={{[content_position]: showDrawer ? '0' : '-100%'}}>
        <div className={style.head}>
            <p className={style.title}>
                {headTitle.length > 0 ? headTitle : title} {count ? `(${count})` : ''}
            </p>
            <span className={style.close} onClick={onCloseDrawer}>
                <Close />
            </span>
        </div>
        <div className={style.content}>
            {displayContent}
        </div>
      </div>
    </div>
  )
};

export default Drawer;
